import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useBreakpointValue } from '@chakra-ui/react';
import { classNames, extractNumbers } from '../../../utils/common';

const formatContactValue = (type, value) => {
  if (type === 'movil') {
    return value?.startsWith('+53') ? value : `+53 ${value}`;
  }

  return value;
};

const getContactKey = (contact, type) =>
  type === 'movil' ? contact.phone || contact.value : contact.email || contact.value;

const ContactInlineSelector = ({
  type,
  contacts,
  inputValue,
  onInputChange,
  onAddContact,
  onRemoveContact,
  options,
  placeholder,
  error,
  icon,
  isLoading,
  expandAll,
  maxVisible: maxVisibleProp,
}) => {
  if (type !== 'movil') return null;

  const containerRef = useRef(null);
  const responsiveMaxVisible = useBreakpointValue({ base: 1, sm: 2 });
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOverflowOpen) return undefined;

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOverflowOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOverflowOpen]);

  const filteredOptions = useMemo(() => {
    const normalizedValue = inputValue.trim().toLowerCase();

    if (!normalizedValue) return [];

    return options
      .filter((option) => {
        const candidate = String(option.value || '').toLowerCase();
        const label = String(option.name || '').toLowerCase();

        return candidate.includes(normalizedValue) || label.includes(normalizedValue);
      })
      .slice(0, 5);
  }, [inputValue, options]);

  const maxVisible = maxVisibleProp ?? responsiveMaxVisible ?? 1;
  const visibleContacts = expandAll ? contacts : contacts.slice(0, maxVisible);
  const hiddenContacts = expandAll ? [] : contacts.slice(maxVisible);

  return (
    <div ref={containerRef} className="w-full">
      <div className="relative">
        <div className="flex items-center rounded-full bg-white px-4 py-2 shadow-sm ring-1 ring-[#D9D9D9]">
          {icon && <span className="mr-3 flex-shrink-0 text-[#B1B1B1]">{icon}</span>}

          <input
            value={inputValue}
            onChange={(event) => {
              const raw = event.target.value;
              const extracted = extractNumbers(raw.trim());

              if (Array.isArray(extracted)) {
                extracted.forEach((numb) => onAddContact({ value: numb }));
                onInputChange('');
              } else {
                onInputChange(raw);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                onAddContact();
              }
            }}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder:text-[#A5A5A5]"
          />


        </div>

        <AnimatePresence>
          {Boolean(inputValue.trim()) && filteredOptions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 right-0 top-full z-[100] mt-2  rounded-2xl bg-white shadow-xl ring-1 ring-black/5"
            >
              {filteredOptions.map((option) => (
                <button
                  key={`${type}-${option.value}`}
                  type="button"
                  onClick={() => onAddContact(option)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-gray-700 transition hover:bg-[#F6F1E8]"
                >
                  <span className="truncate">
                    {formatContactValue(type, option.value)}
                  </span>
                  {option.name && (
                    <span className="ml-3 truncate text-xs text-gray-400">
                      {option.name}
                    </span>
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="relative mt-3">
        <div className={classNames("flex min-w-0 gap-2", expandAll ? "flex-wrap" : "flex-nowrap items-center")}>
          {/* Chips visibles — area con overflow hidden para no salirse del card */}
          <motion.div layout className={classNames("flex min-w-0 gap-2", expandAll ? "flex-wrap" : "flex-nowrap items-center overflow-hidden")}>
            <AnimatePresence initial={false}>
              {visibleContacts.map((contact) => {
                const value = getContactKey(contact, type);

                return (
                  <motion.span
                    key={`${type}-${value}`}
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-[#fef0d5] px-3 py-1 text-xs font-medium text-[#7F4A00]"
                  >
                    <span className="truncate">{formatContactValue(type, value)}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveContact(value)}
                      className="flex-shrink-0 rounded-full text-[#D47A13] transition hover:text-[#a95a08]"
                      aria-label={t('recharge.inlineSelector.removeContact', { value })}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </motion.span>
                );
              })}
            </AnimatePresence>
          </motion.div>

          {/* Botón "+N" fuera del contenedor overflow-hidden para que su dropdown no se recorte */}
          {hiddenContacts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex-shrink-0"
            >
              <button
                type="button"
                onClick={() => setIsOverflowOpen((current) => !current)}
                className="inline-flex items-center gap-1 rounded-full bg-[#fef0d5] px-3 py-1 text-xs font-semibold text-[#7F4A00]"
              >
                +{hiddenContacts.length}
                <ChevronDownIcon
                  className={classNames(
                    'h-4 w-4 transition-transform duration-200',
                    isOverflowOpen && 'rotate-180',
                  )}
                />
              </button>

              <AnimatePresence>
                {isOverflowOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    layout
                    className="absolute bottom-full left-0 z-40 mb-2 w-64 rounded-2xl bg-white p-2 shadow-xl ring-1 ring-black/5"
                  >
                    <motion.div layout className="space-y-2">
                      {hiddenContacts.map((contact) => {
                        const value = getContactKey(contact, type);

                        return (
                          <motion.div
                            key={`${type}-overflow-${value}`}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                            className="flex items-center justify-between gap-2 rounded-xl bg-[#F8F8F8] px-3 py-2 text-sm text-gray-700"
                          >
                            <span className="truncate">{formatContactValue(type, value)}</span>
                            <button
                              type="button"
                              onClick={() => onRemoveContact(value)}
                              className="flex-shrink-0 rounded-full p-1 text-[#D47A13] transition hover:bg-[#FBE9CC] hover:text-[#a95a08]"
                              aria-label={t('recharge.inlineSelector.removeContact', { value })}
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {!contacts.length && !isLoading && (
            <span className="text-xs text-gray-700">
              {t('recharge.inlineSelector.helper')}
            </span>
          )}

          {isLoading && (
            <span className="text-xs text-[#8B8B8B]">
              {t('recharge.inlineSelector.loadingContacts')}
            </span>
          )}
        </div>
      </div>

      {error ? (
        <p className={classNames('mt-2 text-sm text-red-500')}>
          {error}
        </p>
      ) : null}
    </div>
  );
};

ContactInlineSelector.propTypes = {
  type: PropTypes.oneOf(['movil', 'nauta']).isRequired,
  contacts: PropTypes.arrayOf(PropTypes.object).isRequired,
  inputValue: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onAddContact: PropTypes.func.isRequired,
  onRemoveContact: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  placeholder: PropTypes.string.isRequired,
  error: PropTypes.string,
  icon: PropTypes.element,
  isLoading: PropTypes.bool,
  expandAll: PropTypes.bool,
  maxVisible: PropTypes.number,
};

export default ContactInlineSelector;
